import React, { useEffect, useState } from "react";
import { Button, Form, Row, Dropdown } from "react-bootstrap";
import Modal from "../../common/components/ThemedModal";
import { createLeague } from "../../../services/leagueService";
import { FaUsers } from "react-icons/fa6";
import {
	getGameModeAssignments,
	getGameModes,
} from "../../../services/adminService";
import { GoLock } from "react-icons/go";
import {
	CreateLeague,
	Event,
	GameModeAssignment,
	League,
	Season,
	Series,
	Sport,
} from "../../../types/model";
import { Privacy } from "../../../constants/constants";

interface CreateLeagueModalProps {
	show: boolean;
	handleClose: () => void;
	events: Event[];
	sports: Sport[];
	seriesList: Series[];
	season: Season;
	handleCreateLeague: (leagueData: CreateLeague) => void;
}

const CreateLeagueModal: React.FC<CreateLeagueModalProps> = ({
	show,
	handleClose,
	events,
	sports,
	season,
	seriesList,
	handleCreateLeague,
}) => {

	const [leagueData, setLeagueData] = useState<CreateLeague>({
		name: "",
		description: "",
		sport: "",
		series: "",
		season: season?._id ?? "",
		privacy: Privacy.Public,
		prize: "",
		losing_prize: "",
	});

	const [filteredSports, setFilteredSports] = useState<Sport[]>([]);
	const [gameModeAssignments, setGameModeAssignments] = useState<
		GameModeAssignment[]
	>([]);

	useEffect(() => {
		fetchGameAssignments();
	}, []);

	useEffect(() => {
		if (leagueData.series) {
			// Filter sports based on events for the current series
			const sportsForSeries = sports.filter((sport) =>
				events.some(
					(event) =>
						event.series._id === leagueData.series &&
						event.sport._id === sport._id,
				),
			);

			const sportsForAssignments = sportsForSeries.filter((sport: Sport) =>
				gameModeAssignments.some(
					(assignment: GameModeAssignment) =>
						assignment.sport._id === sport._id &&
						assignment.series._id === leagueData.series,
				),
			);
			setFilteredSports(sportsForAssignments);
		} else {
			setFilteredSports([]);
		}
	}, [leagueData.series, sports, events]);

	const fetchGameAssignments = async () => {
		try {
			const res = await getGameModeAssignments();
			setGameModeAssignments(res.data);
		} catch (error) {
			console.error("Error fetching game modes:", error);
		}
	};

	const handleChange = (e: any) => {
		const { name, value } = e.target;
		if (name === "series") {
			// Preserve the selected series when changing the sport
			setLeagueData({ ...leagueData, [name]: value });
		} else {
			setLeagueData({
				...leagueData,
				[name]: value,
				series: leagueData.series,
			}); // Preserve the selected series
		}
	};

	const handleSportChange = (sportId: string) => {
		setLeagueData({ ...leagueData, sport: sportId });
	};

	const handleSeriesChange = (seriesId: string) => {
		setLeagueData({ ...leagueData, series: seriesId });
	};

	const handleSubmit = async (e: any) => {
		e.preventDefault();
		try {
			handleCreateLeague(leagueData);
		} catch (error) {
			console.error("Error creating league:", error);
		}
	};

	return (
		<Modal show={show} size={"lg"} onHide={handleClose}>
			<Modal.Header closeButton className="w-100">
				<Modal.Title>
					{" "}
					<FaUsers className="mb-1 me-2" />
					Create New League
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="px-5 ">
				<Form onSubmit={handleSubmit}>
					<Form.Group className="mb-3" controlId="name">
						<Form.Label>Name</Form.Label>
						<Form.Control
							type="text"
							placeholder="Enter league name"
							name="name"
							value={leagueData.name}
							onChange={handleChange}
							required
						/>
					</Form.Group>

					<Form.Group className="mb-3" controlId="description">
						<Form.Label>Description</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							placeholder="Enter description"
							name="description"
							value={leagueData.description}
							onChange={handleChange}
						/>
					</Form.Group>
					<Form.Group className="mb-3" controlId="season">
						<Form.Control type="text" value={season?.name} disabled />
					</Form.Group>
					<Form.Group className="mb-3" controlId="series">
						<Dropdown>
							<Dropdown.Toggle variant="outline-dark" className="w-100">
								{leagueData.series
									? seriesList.find(
											(series) => series._id === leagueData.series,
									  )?.name
									: "Select Series"}
							</Dropdown.Toggle>
							<Dropdown.Menu>
								{seriesList.map((series: Series) => (
									<Dropdown.Item
										key={series._id}
										onClick={() => handleSeriesChange(series._id ?? "")}
									>
										{series.name}
									</Dropdown.Item>
								))}
							</Dropdown.Menu>
						</Dropdown>
					</Form.Group>

					<Form.Group className="mb-3" controlId="sport">
						<Dropdown>
							<Dropdown.Toggle variant="outline-dark" className="w-100">
								{leagueData.sport
									? filteredSports.find(
											(sport) => sport._id === leagueData.sport,
									  )?.name
									: "Select Discipline"}
							</Dropdown.Toggle>
							<Dropdown.Menu>
								{filteredSports.map((sport) => (
									<Dropdown.Item
										key={sport._id}
										onClick={() => sport._id && handleSportChange(sport._id)}
									>
										{sport.name}
									</Dropdown.Item>
								))}
							</Dropdown.Menu>
						</Dropdown>
						{filteredSports.length == 0 && leagueData.series && (
							<p className="text-center text-muted mt-1">
								<GoLock className="mb-1 me-2" />
								Coming soon...
							</p>
						)}
					</Form.Group>

					<Form.Group className="mb-3" controlId="privacy">
						<Form.Label>Privacy</Form.Label>
						<Form.Select
							name="privacy"
							value={leagueData.privacy}
							onChange={handleChange}
						>
							<option value={Privacy.Public}>Public</option>
							<option value={Privacy.Private}>Private</option>
						</Form.Select>
						{leagueData.privacy === Privacy.Private && (
							<Form.Text className="text-muted">
								Private leagues will have their own private URL.
							</Form.Text>
						)}
					</Form.Group>

					<Form.Group className="mb-3" controlId="prize">
						<Form.Label>Prize</Form.Label>
						<Form.Control
							type="text"
							placeholder="Enter prize"
							name="prize"
							value={leagueData.prize}
							onChange={handleChange}
						/>
					</Form.Group>

					<Form.Group className="mb-3" controlId="losing_prize">
						<Form.Label>Losing Prize</Form.Label>
						<Form.Control
							type="text"
							placeholder="Enter losing prize"
							name="losing_prize"
							value={leagueData.losing_prize}
							onChange={handleChange}
						/>
					</Form.Group>
					<div className="d-flex flex-row justify-content-center">
						<Button
							variant="primary"
							className="py-3 px-4 w-auto"
							type="submit"
						>
							Create League
						</Button>
					</div>
				</Form>
			</Modal.Body>
		</Modal>
	);
};

export default CreateLeagueModal;
